/**
 * AppICEMAP — MapViewport Custom Element
 * Full-viewport Leaflet map with marker clustering and heatmap support.
 *
 * Tag: <map-viewport>
 *
 * Attributes:
 *   center          — [lat, lng] initial center (default: SF [37.7749, -122.4194])
 *   zoom            — initial zoom (default: 14)
 *   night-mode      — apply night tile desaturation
 *
 * Dispatches:
 *   map:report-clicked    → { reportId }
 *   map:viewport-changed   → { bounds, zoom }
 *   map:ready              → { }
 *   map:user-location      → { lat, lng }
 */

import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.heat";
import {
	createReportMarker,
	createClusterIcon,
	isNewReport,
	isLowKarmaReport,
	getExpiryOpacity,
} from "./ReportMarker.js";
import { bindPopup, buildPopupContent } from "./PopupCard.js";
import {
	initUserLocation,
	destroyUserLocation,
	getUserPosition,
} from "./UserLocationDot.js";
import { show as showToast } from "../shared/Toast.js";
import { fetchPOIs, POI_TYPES } from "../../services/pois.js";
import { US_MAP_BOUNDS, isInUS } from "../../utils/geo.js";
import { enablePopupDragThrough } from "./popupDragThrough.js";

/** Minimum zoom at which POIs are fetched/shown (avoids huge Overpass queries) */
const POI_MIN_ZOOM = 16;

/**
 * Radius (in degrees) around the map center for POI fetching at POI_MIN_ZOOM.
 * ~0.0045° ≈ 500m.  Halved per zoom level above min.
 */
function _poiRadius(zoom) {
	return 0.0045 * 2 ** Math.max(0, POI_MIN_ZOOM - zoom);
}

/** @type {string} OpenStreetMap tile URL template */
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

/** @type {string} CartoDB dark tiles for night mode */
const TILE_URL_DARK =
	"https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

/** Default center (San Francisco) */
const DEFAULT_CENTER = [37.7749, -122.4194];

/** Default zoom */
const DEFAULT_ZOOM = 14;

/** The map is fenced to US territory — see utils/geo.js */
const MAX_BOUNDS = US_MAP_BOUNDS;

export class MapViewport extends HTMLElement {
	/* ── Lifecycle ──────────────────────────────────────────────────────── */

	constructor() {
		super();
		/** @type {L.Map | null} */
		this.map = null;
		/** @type {L.TileLayer | null} */
		this.tileLayer = null;
		/** @type {Map<string, L.MarkerClusterGroup>} one cluster group per report category slug */
		this.clusterGroupsByCategory = new Map();
		/** @type {L.HeatLayer | null} */
		this.heatmapLayer = null;
		/** @type {Map<string, L.Marker>} */
		this.markersById = new Map();
		/** @type {Map<string, string>} report id -> category slug (to find its cluster group on update/remove) */
		this.categoryByReportId = new Map();
		/** @type {L.LayerGroup | null} POI background layer */
		this.poiLayer = null;
		/** @type {number | null} debounce timer for POI fetches */
		this._poiTimer = null;
		/** @type {string | null} last fetched POI bbox key */
		this._poiKey = null;
		/** @type {Set<string>} POI IDs already rendered (accumulate nearby) */
		this._renderedPoiIds = new Set();
		/** @type {Function | null} */
		this._themeCleanup = null;
		/** @type {Function | null} */
		this._dragThroughCleanup = null;
	}

	connectedCallback() {
		this._initMap();
	}

	disconnectedCallback() {
		destroyUserLocation();
		if (this._themeCleanup) this._themeCleanup();
		if (this._dragThroughCleanup) this._dragThroughCleanup();
		if (this.map) {
			this.map.remove();
			this.map = null;
		}
	}

	/* ── Map Init ───────────────────────────────────────────────────────── */

	_initMap() {
		// Create container
		const container = document.createElement("div");
		container.id = "map";
		container.style.cssText = "width:100%;height:100%;";
		this.appendChild(container);

		// Get initial center/zoom from attributes or defaults
		let center = DEFAULT_CENTER;
		const centerAttr = this.getAttribute("center");
		if (centerAttr) {
			try {
				const parsed = JSON.parse(centerAttr);
				if (Array.isArray(parsed) && parsed.length === 2) {
					center = parsed;
				}
			} catch {
				/* use default */
			}
		}

		// Never open outside the country, whatever the attribute says
		if (!isInUS(center[0], center[1])) {
			center = DEFAULT_CENTER;
		}

		const zoom = parseInt(this.getAttribute("zoom"), 10) || DEFAULT_ZOOM;

		// Create Leaflet map
		this.map = L.map(container, {
			center,
			zoom,
			zoomControl: false,
			attributionControl: true,
			maxBounds: MAX_BOUNDS,
			maxBoundsViscosity: 0.8,
		});

		// Zoom controls (styled in map-viewer.css)
		L.control.zoom({ position: "topleft" }).addTo(this.map);

		// Locate-me button (custom Leaflet control)
		const LocateControl = L.Control.extend({
			onAdd: () => {
				const btn = L.DomUtil.create("a", "leaflet-control-locate");
				btn.href = "#";
				btn.title = "Ir a mi ubicación";
				btn.setAttribute("role", "button");
				btn.setAttribute("aria-label", "Ir a mi ubicación");
				btn.innerHTML = '<i class="ph ph-crosshair" aria-hidden="true"></i>';

				// El botón no debe arrastrar el mapa ni navegar por el href
				L.DomEvent.disableClickPropagation(btn);

				L.DomEvent.on(btn, "click", (e) => {
					L.DomEvent.preventDefault(e);

					// La app ya sigue tu posición con watchPosition: si la tiene,
					// se usa al instante en vez de pedir otro fix (que en móvil
					// puede tardar segundos o fallar en silencio).
					const conocida = getUserPosition();
					if (conocida) {
						this.map.setView(conocida, Math.max(this.map.getZoom(), 16), {
							animate: false,
						});
						return;
					}

					btn.classList.add("leaflet-control-locate--locating");
					this.map.locate({
						setView: true,
						maxZoom: 18,
						enableHighAccuracy: true,
						timeout: 8000,
					});
				});

				const stopSpinner = () =>
					btn.classList.remove("leaflet-control-locate--locating");
				this.map.on("locationfound", stopSpinner);

				// Sin este aviso el botón parecía roto: se pulsaba y no ocurría
				// nada, ni movimiento ni explicación.
				this.map.on("locationerror", (err) => {
					stopSpinner();
					showToast(
						err?.code === 1
							? "Ubicación bloqueada. Actívala en el candado de la barra de direcciones."
							: "No se pudo obtener tu ubicación. Comprueba el GPS.",
						"warning",
						7000,
					);
				});

				return btn;
			},
		});

		new LocateControl({ position: "topleft" }).addTo(this.map);

		// Add attribution
		L.control
			.attribution({
				position: "bottomleft",
				prefix: false,
			})
			.addTo(this.map);

		// Determine tile layer based on night mode
		this._updateTileLayer();

		// POI background layer (kept below report markers by adding it first)
		this.poiLayer = L.layerGroup();
		this.map.addLayer(this.poiLayer);

		// Initialize user location
		initUserLocation(this.map);

		// Track viewport changes
		this.map.on("moveend", () => {
			this._onViewportChanged();
			this._maybeFetchPOIs();
		});

		// Initial POI load
		this._maybeFetchPOIs();

		// Listen for theme changes
		this._listenToTheme();

		// Let a drag started on top of an open popup card still pan the map
		// (see popupDragThrough.js for why that's not the default behaviour).
		this._dragThroughCleanup = enablePopupDragThrough(this.map);

		// Map ready — dispatch on the next tick. connectedCallback (and thus
		// _initMap) runs synchronously during appendChild, BEFORE AppShell
		// attaches its {once} "map:ready" listener; firing synchronously would
		// miss it and the initial viewport bounds / invalidateSize would never
		// run (reports then only load after a manual map move).
		setTimeout(() => {
			this.dispatchEvent(
				new CustomEvent("map:ready", { bubbles: true, detail: {} }),
			);
		}, 0);

		console.log("[MapViewport] Initialized");
	}

	/* ── Tile Layer ─────────────────────────────────────────────────────── */

	_updateTileLayer() {
		const nightMode = this.hasAttribute("night-mode");

		if (this.tileLayer && this.map) {
			this.map.removeLayer(this.tileLayer);
		}

		const tileUrl = nightMode ? TILE_URL_DARK : TILE_URL;

		this.tileLayer = L.tileLayer(tileUrl, {
			attribution:
				'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
			maxZoom: 19,
		});

		if (this.map) {
			this.tileLayer.addTo(this.map);
		}

		// Night mode: apply CSS filter to dim tiles
		if (nightMode && this.map) {
			const tileContainer = this.map.getPane("tilePane");
			if (tileContainer) {
				tileContainer.style.filter = "saturate(0.7) brightness(0.85)";
			}
		} else if (this.map) {
			const tileContainer = this.map.getPane("tilePane");
			if (tileContainer) {
				tileContainer.style.filter = "";
			}
		}
	}

	_listenToTheme() {
		const observer = new MutationObserver(() => {
			const theme = document.documentElement.dataset.theme;
			if (theme === "night") {
				this.setAttribute("night-mode", "");
			} else {
				this.removeAttribute("night-mode");
			}
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["data-theme"],
		});

		// Initial check
		if (document.documentElement.dataset.theme === "night") {
			this.setAttribute("night-mode", "");
		}
	}

	/* ── Attribute Changes ─────────────────────────────────────────────── */

	static get observedAttributes() {
		return ["night-mode", "center", "zoom"];
	}

	attributeChangedCallback(name, oldVal, newVal) {
		if (oldVal === newVal) return;

		if (name === "night-mode") {
			this._updateTileLayer();
		}

		if (name === "center" && this.map) {
			try {
				const parsed = JSON.parse(newVal);
				if (Array.isArray(parsed) && parsed.length === 2) {
					this.map.setView(parsed, this.map.getZoom(), {
						animate: true,
						duration: 0.6,
					});
				}
			} catch {
				/* ignore */
			}
		}

		if (name === "zoom" && this.map && newVal) {
			const zoom = parseInt(newVal, 10);
			if (!isNaN(zoom)) {
				this.map.setZoom(zoom, { animate: true });
			}
		}
	}

	/* ── Reports API ───────────────────────────────────────────────────── */

	/**
	 * Get (or lazily create) the cluster group for a report category, so that
	 * markers only ever cluster with others of the same type (e.g. ICE with
	 * ICE, never ICE with Police).
	 * @param {string} categorySlug
	 * @returns {L.MarkerClusterGroup}
	 */
	_getOrCreateClusterGroup(categorySlug) {
		let group = this.clusterGroupsByCategory.get(categorySlug);
		if (group) return group;

		group = L.markerClusterGroup({
			maxClusterRadius: 50,
			spiderfyOnMaxZoom: true,
			showCoverageOnHover: false,
			zoomToBoundsOnClick: true,
			iconCreateFunction: (cluster) => createClusterIcon(cluster),
		});

		group.on("click", (e) => {
			if (e.layer && e.layer.getLatLng) {
				// It's an individual marker, not a cluster
				const markerEl = e.layer.getElement();
				if (markerEl) {
					const reportId = markerEl.dataset?.reportId;
					if (reportId) {
						this.dispatchEvent(
							new CustomEvent("map:report-clicked", {
								detail: { reportId },
								bubbles: true,
							}),
						);
					}
				}
			}
		});

		this.clusterGroupsByCategory.set(categorySlug, group);
		if (this.map) this.map.addLayer(group);
		return group;
	}

	/**
	 * Update markers from a reports array.
	 * Incremental diff: adds new, updates changed, removes gone.
	 * @param {Array} reports
	 * Tocar un marcador abre la ficha completa (evento map:report-clicked), así
	 * que la tarjeta emergente ya no necesita botones propios.
	 */
	updateReports(reports) {
		if (!this.map) return;

		const list = Array.isArray(reports) ? reports : [];
		const incomingIds = new Set(list.map((r) => r.id));
		const currentIds = new Set(this.markersById.keys());

		// Remove markers no longer in reports
		for (const id of currentIds) {
			if (!incomingIds.has(id)) {
				const marker = this.markersById.get(id);
				const prevSlug = this.categoryByReportId.get(id);
				const group = this.clusterGroupsByCategory.get(prevSlug);
				if (marker && group) {
					group.removeLayer(marker);
				}
				this.markersById.delete(id);
				this.categoryByReportId.delete(id);
			}
		}

		// Add or update markers
		for (const report of list) {
			if (!report.location?.coordinates) continue;

			const [lng, lat] = report.location.coordinates;
			const latlng = [lat, lng];

			const pulseNew = isNewReport(report);
			const lowKarma = isLowKarmaReport(report);
			const expiryOpacity = getExpiryOpacity(report);

			const icon = createReportMarker(report, {
				pulseNew,
				lowKarma,
				expiryOpacity,
			});

			const categorySlug = report.category?.slug || "other";
			const existing = this.markersById.get(report.id);

			if (existing) {
				const prevSlug = this.categoryByReportId.get(report.id);

				if (prevSlug !== categorySlug) {
					// Category changed — move marker to the matching cluster group
					const prevGroup = this.clusterGroupsByCategory.get(prevSlug);
					if (prevGroup) prevGroup.removeLayer(existing);
					this._getOrCreateClusterGroup(categorySlug).addLayer(existing);
					this.categoryByReportId.set(report.id, categorySlug);
				}

				// Update marker
				existing.setLatLng(latlng);
				existing.setIcon(icon);

				// Update popup content
				if (existing.getPopup()) {
					existing.setPopupContent(buildPopupContent(report));
				}
			} else {
				// Create new marker
				const marker = L.marker(latlng, {
					icon,
				});

				bindPopup(marker, report);

				// Store report ID on the marker element for cluster click handling
				marker.on("add", () => {
					const el = marker.getElement();
					if (el) {
						el.dataset.reportId = report.id;
					}
				});

				this._getOrCreateClusterGroup(categorySlug).addLayer(marker);
				this.markersById.set(report.id, marker);
				this.categoryByReportId.set(report.id, categorySlug);
			}
		}
	}

	/**
	 * Clear all markers.
	 */
	clearMarkers() {
		for (const group of this.clusterGroupsByCategory.values()) {
			group.clearLayers();
		}
		this.markersById.clear();
		this.categoryByReportId.clear();
	}

	/**
	 * Fly to a specific report marker.
	 * @param {string} reportId
	 */
	flyToReport(reportId) {
		const marker = this.markersById.get(reportId);
		if (marker && this.map) {
			const latlng = marker.getLatLng();
			this.map.flyTo(latlng, 16, {
				duration: 0.8,
			});
			marker.openPopup();
		}
	}

	/* ── Points of Interest (default map POIs) ─────────────────────────── */

	/**
	 * Fetch + render nearby POIs around the map centre, debounced and zoom-gated.
	 * Only loads POIs within a small radius from the centre at high zoom,
	 * accumulating them as the user pans (avoids large Overpass queries).
	 */
	_maybeFetchPOIs() {
		if (!this.map || !this.poiLayer) return;

		const zoom = this.map.getZoom();

		// Below the min zoom — clear everything
		if (zoom < POI_MIN_ZOOM) {
			this.poiLayer.clearLayers();
			this._renderedPoiIds.clear();
			this._poiKey = null;
			return;
		}

		if (this._poiTimer) clearTimeout(this._poiTimer);
		this._poiTimer = setTimeout(async () => {
			const center = this.map.getCenter();
			const radius = _poiRadius(zoom);
			const sw = [center.lat - radius, center.lng - radius];
			const ne = [center.lat + radius, center.lng + radius];
			const key = `${sw[0].toFixed(3)},${sw[1].toFixed(3)},${ne[0].toFixed(3)},${ne[1].toFixed(3)}`;
			if (key === this._poiKey) return; // same area
			this._poiKey = key;

			try {
				const pois = await fetchPOIs({ sw, ne });
				this._renderPOIs(pois);
			} catch (err) {
				console.warn("[MapViewport] POI fetch failed:", err);
			}
		}, 1000);
	}

	/**
	 * Render POI markers into the POI layer (accumulates — old POIs are kept
	 * as the user pans; only cleared when zoom drops below POI_MIN_ZOOM).
	 * @param {Array<{id:string,lat:number,lng:number,type:string,name:string}>} pois
	 */
	_renderPOIs(pois) {
		if (!this.poiLayer) return;
		if (!pois || pois.length === 0) return;

		for (const poi of pois) {
			if (this._renderedPoiIds.has(poi.id)) continue;
			this._renderedPoiIds.add(poi.id);
			const cfg = POI_TYPES[poi.type];
			if (!cfg) continue;

			const icon = L.divIcon({
				className: "poi-marker",
				html: `<span class="poi-pin" style="--poi-color:${cfg.color}" title="${escapeAttr(poi.name)}"><i class="ph ph-${cfg.icon}"></i></span>`,
				iconSize: [26, 26],
				iconAnchor: [13, 13],
				popupAnchor: [0, -13],
			});

			const marker = L.marker([poi.lat, poi.lng], {
				icon,
				keyboard: false,
				// Below report markers
				zIndexOffset: -500,
			});

			marker.bindPopup(
				`<div class="poi-popup"><span class="poi-popup__type" style="color:${cfg.color}">${cfg.label}</span><strong class="poi-popup__name">${escapeHtml(poi.name)}</strong></div>`,
				{ className: "poi-popup-wrapper", closeButton: false },
			);

			this.poiLayer.addLayer(marker);
		}
	}

	/**
	 * Toggle POI visibility.
	 * @param {boolean} visible
	 */
	setPOIsVisible(visible) {
		if (!this.map || !this.poiLayer) return;
		if (visible) {
			if (!this.map.hasLayer(this.poiLayer)) this.map.addLayer(this.poiLayer);
			this._maybeFetchPOIs();
		} else if (this.map.hasLayer(this.poiLayer)) {
			this.map.removeLayer(this.poiLayer);
		}
	}

	/* ── Heatmap ───────────────────────────────────────────────────────── */

	/**
	 * Show a heatmap layer based on report data.
	 * @param {Array<[number, number, number]>} points - [lat, lng, intensity]
	 */
	showHeatmap(points) {
		if (!this.map) return;

		if (this.heatmapLayer) {
			this.map.removeLayer(this.heatmapLayer);
		}

		if (!points || points.length === 0) return;

		this.heatmapLayer = L.heatLayer(points, {
			radius: 25,
			blur: 15,
			maxZoom: 18,
			gradient: {
				0.4: "blue",
				0.6: "cyan",
				0.7: "lime",
				0.8: "yellow",
				1.0: "red",
			},
		}).addTo(this.map);
	}

	hideHeatmap() {
		if (this.heatmapLayer && this.map) {
			this.map.removeLayer(this.heatmapLayer);
			this.heatmapLayer = null;
		}
	}

	/* ── Viewport ──────────────────────────────────────────────────────── */

	_onViewportChanged() {
		if (!this.map) return;

		const bounds = this.map.getBounds();
		const sw = bounds.getSouthWest();
		const ne = bounds.getNorthEast();

		this.dispatchEvent(
			new CustomEvent("map:viewport-changed", {
				detail: {
					bounds: {
						sw: [sw.lat, sw.lng],
						ne: [ne.lat, ne.lng],
					},
					zoom: this.map.getZoom(),
				},
				bubbles: true,
			}),
		);
	}

	/**
	 * Get current viewport bounds for API fetching.
	 * @returns {{ sw: [number, number], ne: [number, number] }}
	 */
	getBounds() {
		if (!this.map) {
			return {
				sw: [-90, -180],
				ne: [90, 180],
			};
		}
		const bounds = this.map.getBounds();
		return {
			sw: [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
			ne: [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
		};
	}

	/**
	 * Invalidate size (call after container resize).
	 */
	invalidateSize() {
		if (this.map) {
			this.map.invalidateSize();
		}
	}
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str == null ? "" : String(str);
	return div.innerHTML;
}

function escapeAttr(str) {
	return escapeHtml(str).replace(/"/g, "&quot;");
}

customElements.define("map-viewport", MapViewport);
